import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import MuiRefreshIcon from '@mui/icons-material/Refresh';

import type { PrinterStatus } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

// ----------------------------------------------------------------------

interface PrinterStatusCardProps {
  status: PrinterStatus | null;
  connected: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}

export default function PrinterStatusCard({
  status,
  connected,
  refreshing,
  onRefresh,
}: PrinterStatusCardProps) {
  const chips: { label: string; color: 'success' | 'error' | 'warning' | 'default' }[] = [
    connected
      ? { label: 'Connected', color: 'success' }
      : { label: 'Disconnected', color: 'default' },
  ];

  if (status) {
    chips.push(
      status.online ? { label: 'Online', color: 'success' } : { label: 'Offline', color: 'error' }
    );
    // A printer that does not report its condition is shown as exactly that,
    // never as "paper OK": the flags below are only meaningful when reported.
    if (status.statusReporting === 'unsupported') {
      chips.push({ label: 'Paper and cover status not reported', color: 'default' });
    } else if (status.statusReporting === 'limited') {
      // A Windows print queue: only what Windows says, and only when it says it.
      chips.push({ label: 'Status from Windows only', color: 'default' });
      if (status.coverOpen) chips.push({ label: 'Cover open (Windows)', color: 'warning' });
      if (status.paperEmpty) chips.push({ label: 'No paper (Windows)', color: 'error' });
    } else if (status.statusReporting === 'unknown') {
      chips.push({ label: 'Status not reported yet', color: 'default' });
    } else {
      if (status.coverOpen) chips.push({ label: 'Cover open', color: 'warning' });
      if (status.paperEmpty) chips.push({ label: 'No paper', color: 'error' });
      else if (status.paperNearEmpty) chips.push({ label: 'Low paper', color: 'warning' });
      if (status.cutterError) chips.push({ label: 'Cutter error', color: 'error' });
      if (status.cashDrawerOpen ?? status.drawerOpen)
        chips.push({ label: 'Drawer open', color: 'default' });
    }
  }

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1">Printer Status</Typography>
        <LoadingButton
          size="small"
          variant="outlined"
          loading={refreshing}
          onClick={onRefresh}
          startIcon={<MuiIcon icon={MuiRefreshIcon} />}
        >
          Refresh
        </LoadingButton>
      </Stack>

      {status || connected ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {chips.map((chip) => (
            <Chip
              key={chip.label}
              label={chip.label}
              color={chip.color}
              size="small"
              variant="soft"
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No status yet. Select a printer and refresh to read its status.
        </Typography>
      )}
    </Card>
  );
}
